import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxIconName } from '@onyx/angular';
import {
  OnyxDataWidgetComponent,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxLinkComponent,
  OnyxNamePipe,
  OnyxSpinnerComponent,
  OnyxTemperaturePipe,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { UnitCellComponent } from '../../../../../../../common/components/cells/unit-cell/unit-cell.component';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { FleetIdentifierPipe } from '../../../../../../../common/pipes/fleet-identifier.pipe';
import { JoinPipe } from '../../../../../../../common/pipes/join.pipe';
import { TelematicsStore } from '../../../../../../../common/stores/telematics.store';
import { DriverHelper } from '../../../../../../drivers/common/helpers/driver.helper';
import type {
  Driver,
  SimplifiedDriver,
} from '../../../../../../drivers/common/interfaces/driver';
import { FleetDescriptionParametersComponent } from '../../../../../../fleet/common/components/fleet-description-parameters/fleet-description-parameters.component';
import { FleetCategory } from '../../../../../../fleet/common/enums/fleet-category';
import { SemiTrailerSize } from '../../../../../../fleet/common/enums/semi-trailer-size';
import { FleetHelper } from '../../../../../../fleet/common/helpers/fleet.helper';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../../../../fleet/common/interfaces/fleet';
import { SwapModalHelper } from '../../../../../../map/common/helpers/swap/swap-modal.helper';
import { TelematicsHelper } from '../../../../../../map/common/helpers/telematics.helper';
import { OrderMissingAssignmentType } from '../../../../../common/enums/order-missing-assignment-type';
import { OrderStatus } from '../../../../../common/enums/order-status';
import { OrderModalHelper } from '../../../../../common/helpers/order-modal.helper';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../common/interfaces/order';
import { ModuleLinkComponent } from '../../../../../../../common/components/module-link/module-link.component';

@Component({
  selector: 'app-order-modal-vehicles',
  imports: [
    ModuleLinkComponent,
    TranslatePipe,
    FleetIdentifierPipe,
    OnyxIconComponent,
    OnyxDataWidgetComponent,
    OnyxTemperaturePipe,
    JoinPipe,
    FleetDescriptionParametersComponent,
    OnyxTooltipDirective,
    UnitCellComponent,
    OnyxInformationHeadingComponent,
    NgTemplateOutlet,
    OnyxIconButtonComponent,
    OnyxNamePipe,
    DecimalPipe,
    NgTemplateOutlet,
    OnyxLinkComponent,
    OnyxSpinnerComponent,
  ],
  templateUrl: './order-modal-vehicles.component.html',
  styleUrl: './order-modal-vehicles.component.scss',
})
export class OrderModalVehiclesComponent {
  protected readonly fleetHelper = inject(FleetHelper);
  private readonly driverHelper = inject(DriverHelper);
  private readonly translateService = inject(TranslateService);
  private readonly orderModalHelper = inject(OrderModalHelper);
  private readonly swapModalHelper = inject(SwapModalHelper);
  private readonly telematicsStore = inject(TelematicsStore);

  public readonly order = input.required<Order>();
  public readonly vehicle = input<Fleet | SimplifiedFleet | null>(null);
  public readonly trailer = input<SimplifiedFleet | null>(null);
  public readonly drivers = input<(Driver | SimplifiedDriver)[] | null>(null);
  public readonly driversLoading = input(true);
  public readonly showHeading = input(true);
  public readonly status = input<OrderStatus | null>();
  public readonly close$ = input.required<Subject<void>>();

  public readonly invalidChange = output<boolean>();

  protected readonly I18N = 'orders.orderModal';
  protected readonly HINT_UNITS: Record<string, string> = {
    loadCapacity: 'kg',
    cubature: 'm³',
    europalletsNumber: 'units.pieces',
    loadingMeters: 'LDM',
  };

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly SemiTrailerSize = SemiTrailerSize;
  protected readonly FleetCategory = FleetCategory;
  protected readonly OrderStatus = OrderStatus;
  protected readonly OrderMissingAssignmentType = OrderMissingAssignmentType;

  protected searchingVehicleData = computed(() =>
    OrderModalHelper.getSearchingVehicleData(
      this.order(),
      this.translateService,
    ),
  );
  protected hasApprovedVehicle = computed(() =>
    OrderHelper.hasApprovedVehicle(this.order().status.value),
  );
  protected hasLoadedSemiTrailer = computed(() =>
    OrderHelper.hasLoadedSemiTrailer(this.order()),
  );
  protected isValidAssignedTrailerType = computed(() =>
    this.searchingVehicleData().types?.includes(
      this.trailer()?.generalInformation?.type ?? '',
    ),
  );
  protected vehicleParameters = computed(() =>
    this.orderModalHelper.getSearchingVehicleParameters(
      this.order(),
      this.vehicle(),
      this.trailer(),
    ),
  );
  protected vehicleInvalidParameters = computed(() =>
    this.vehicleParameters().filter(({ ok }) => !ok()),
  );
  protected isVehicleInvalid = computed(
    () => this.vehicle() && this.vehicleParameters().some(({ ok }) => !ok()),
  );
  protected trailerParameters = computed(() =>
    this.orderModalHelper.getSearchingTrailerParameters(
      this.order(),
      this.trailer(),
    ),
  );
  protected trailerInvalidParameters = computed(() =>
    this.trailerParameters().filter(({ ok }) => !ok()),
  );
  protected searchingTrailerTypes = computed(() =>
    OrderModalHelper.getSearchingTrailerTypes(this.order()),
  );
  protected isTrailerInvalid = computed(
    () => this.trailer() && this.trailerParameters().some(({ ok }) => !ok()),
  );
  protected driverParameters = computed(() =>
    this.orderModalHelper.getSearchingDriversParameters(
      this.order(),
      this.drivers() ?? [],
    ),
  );
  protected areDriversInvalid = computed(
    () =>
      !!this.drivers()?.length &&
      this.driverParameters().some(({ ok }) => !ok()),
  );
  protected isSectionVisible = computed(
    () =>
      !this.hasApprovedVehicle() ||
      this.vehicle() ||
      this.hasLoadedSemiTrailer() ||
      this.trailer(),
  );
  protected isActive = computed(() => OrderHelper.isActive(this.order()));
  protected missingAssignment = computed(() =>
    OrderHelper.getMissingAssignment(this.order()),
  );
  protected planChangeButton = computed(() => {
    const target = this.planChangeTarget();
    if (!target) return null;

    return {
      disabled: target.hasPlannedSwap,
      tooltip: target.hasPlannedSwap
        ? 'issues.swapExists'
        : 'labels.planChange',
    };
  });
  private planChangeTarget = computed(() => {
    if (!this.isActive() || !this.fleetHelper.hasModule()) return null;

    const item = this.telematicsStore.getItem(this.vehicle()?.uuid);
    if (!item) return null;

    const delegation = TelematicsHelper.getDelegation(item);
    if (!this.fleetHelper.canPlanChange(item, delegation)) return null;

    return {
      item,
      delegation,
      hasPlannedSwap: TelematicsHelper.hasSwap(item, this.telematicsStore),
    };
  });

  constructor() {
    effect(() => {
      if (!this.vehicle()) return;

      this.invalidChange.emit(
        !!(
          this.isVehicleInvalid() ||
          this.isTrailerInvalid() ||
          this.areDriversInvalid()
        ),
      );
    });
  }

  protected getDriverIcon(driver: Driver | SimplifiedDriver): OnyxIconName {
    return TelematicsHelper.getDriverIcon(driver);
  }

  protected openDriverModal(driver: Driver | SimplifiedDriver): void {
    this.driverHelper.openModal(driver);
  }

  protected openFleetModal(fleet: SimplifiedFleet): void {
    this.fleetHelper.openModal(fleet);
  }

  protected openActivityModal(): void {
    this.orderModalHelper.openActivityModal({ order: this.order() });
  }

  protected planChange(): void {
    const target = this.planChangeTarget();
    if (!target) return;

    this.fleetHelper.changeVehicleSet(target.item, {
      delegation: target.delegation,
      close$: this.close$(),
    });
  }

  protected openSwapModal(): void {
    const uuid = this.order().relatedFleetSwap?.uuid;
    if (uuid) {
      this.swapModalHelper.openModal(uuid, this.vehicle()?.uuid ?? null);
    }
  }
}
